import type { MessengerParentSize } from '@widgets/messenger/types'
import { messengerWidgetWidth, messengerMobileSideAndBottomSpacing } from '@widgets/messenger/const'
import { isInIframe } from '@widgets/shared/utils/isInIframe'
import { useWidgetPostMessages } from '@widgets/messenger/utils/postMessages'

const useSendParentResizeMessages = (widgetId: string) =>
  useWidgetPostMessages<MessengerParentSize>(widgetId, 'textmagicSendParentResize')

const useGetParentResizeMessages = (widgetId: string) =>
  useWidgetPostMessages<undefined>(widgetId, 'textmagicGetParentResize')

const maxMobileWidth = 768
const maxMessengerWidgetWidth = messengerWidgetWidth + messengerMobileSideAndBottomSpacing * 2

export const checkIsMobileParentWindow = (bodyHtml: HTMLElement) => bodyHtml.offsetWidth <= maxMobileWidth
export const checkIsParentSmaller = (bodyHtml: HTMLElement) => bodyHtml.offsetWidth <= maxMessengerWidgetWidth

export const getParentWindowSize = (bodyHtml: HTMLElement): MessengerParentSize => {
  return {
    isMobile: checkIsMobileParentWindow(bodyHtml),
    isParentSmaller: checkIsParentSmaller(bodyHtml),
  }
}

export const sendMessengerParentPositionMessage = (
  widgetId: string,
  iframe: HTMLIFrameElement,
  position: MessengerParentSize,
) => {
  useSendParentResizeMessages(widgetId).sendToIframe(iframe, position)
}

export const subscribeOnMessengerParentResizeMessages = (
  widgetId: string,
  callback: (payload: MessengerParentSize) => void,
) => {
  return useSendParentResizeMessages(widgetId).subscribe((event) => {
    callback(event.data.payload)
  })
}

export const subscribeOnParentResize = (bodyHtml: HTMLElement, callback: (payload: MessengerParentSize) => void) => {
  const resizeObserver = new ResizeObserver(() => {
    callback(getParentWindowSize(bodyHtml))
  })
  resizeObserver.observe(bodyHtml)
  return () => resizeObserver.disconnect()
}

export const subscribeAndHandleMessengerGetParentResizeMessages = (widgetId: string, bodyHtml: HTMLElement) => {
  return useGetParentResizeMessages(widgetId).subscribe((event) => {
    useSendParentResizeMessages(widgetId).reply(event, getParentWindowSize(bodyHtml))
  })
}

export const getParentSize = async (widgetId: string): Promise<MessengerParentSize | undefined> => {
  if (!isInIframe()) {
    return undefined
  }

  useGetParentResizeMessages(widgetId).sendToParent(undefined)

  return new Promise<MessengerParentSize>((resolve) => {
    useSendParentResizeMessages(widgetId).subscribe((event) => {
      resolve(event.data.payload)
    })
  })
}
